import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './RichTextEditor.css';
import { toast } from 'react-toastify';
import { Code2, Eye, FileText, PenLine } from 'lucide-react';
import { sanitizeBlogHtml } from '../../../../utils/blogHtml';
import { registerQuillPdfBlot } from '../../../../utils/registerQuillPdfBlot';
import { blogPdfFileName, resolveBlogPdfUrl, sanitizeBlogPdfPath } from '../../../../utils/blogPdf';

registerQuillPdfBlot();

function previewHtmlWithPendingNotes(html) {
    const raw = typeof html === "string" ? html : "";
    if (!raw.includes("oa-pdf-pending")) return raw;
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<div>${raw}</div>`, "text/html");
    const root = doc.body.firstChild;
    if (!root) return raw;
    root.querySelectorAll(".oa-pdf-pending[data-pdf-pending-id]").forEach((el) => {
        const name = el.getAttribute("data-pdf-label") || "PDF";
        const p = doc.createElement("p");
        p.className = "oa-pdf-pending-preview-note";
        const em = doc.createElement("em");
        em.textContent = name;
        p.appendChild(em);
        p.appendChild(doc.createTextNode(" — uploads when you save"));
        el.replaceWith(p);
    });
    return root.innerHTML;
}

/** Isolated so a ticking clock does not re-render ReactQuill every second (that caused scroll jumps). */
function RteLiveClock() {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const intervalId = window.setInterval(() => setNow(new Date()), 1000);
        return () => window.clearInterval(intervalId);
    }, []);

    return <span>Updated {now.toLocaleTimeString()}</span>;
}

export default function RichTextEditor({ initialContent = '', onChange, pendingPdfFilesRef }) {
    const quillRef = useRef(null);
    const pdfInputRef = useRef(null);
    const [content, setContent] = useState(initialContent);
    const [wordCount, setWordCount] = useState(0);
    const [charCount, setCharCount] = useState(0);
    const [showSourceCode, setShowSourceCode] = useState(false);
    const [previewMode, setPreviewMode] = useState('editor'); // 'editor' or 'preview'
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const lastInitialContentRef = useRef(initialContent);

    const internalPendingRef = useRef(new Map());
    const pendingMap = pendingPdfFilesRef || internalPendingRef;

    useEffect(() => {
        // If the loaded blog content changes (e.g. switching posts / async fetch),
        // reset the editor. During normal typing, the parent echoes the same HTML
        // back down so this will not run.
        if (lastInitialContentRef.current === initialContent) return;
        lastInitialContentRef.current = initialContent;
        setHasUserEdited(false);
        setContent(initialContent || '');
    }, [initialContent]);

    /** Custom toolbar controls are icon-only — native tooltips for table + PDF. */
    useEffect(() => {
        let cancelled = false;
        let innerT;
        const tryLabel = () => {
            const editor = quillRef.current?.getEditor?.();
            const toolbar = editor?.getModule?.('toolbar')?.container;
            if (!toolbar) return false;
            const tableBtn = toolbar.querySelector('button.ql-insertTable');
            const colLeftBtn = toolbar.querySelector('button.ql-insertColumnLeft');
            const colRightBtn = toolbar.querySelector('button.ql-insertColumnRight');
            const rowAboveBtn = toolbar.querySelector('button.ql-insertRowAbove');
            const rowBelowBtn = toolbar.querySelector('button.ql-insertRowBelow');
            const pdfBtn = toolbar.querySelector('button.ql-insertPdf');
            if (tableBtn) {
                tableBtn.setAttribute('title', 'Insert table (3×3)');
                tableBtn.setAttribute('aria-label', 'Insert table');
            }
            if (colLeftBtn) {
                colLeftBtn.setAttribute('title', 'Insert column left (cursor in a table cell)');
                colLeftBtn.setAttribute('aria-label', 'Insert column left');
            }
            if (colRightBtn) {
                colRightBtn.setAttribute('title', 'Insert column right (cursor in a table cell)');
                colRightBtn.setAttribute('aria-label', 'Insert column right');
            }
            if (rowAboveBtn) {
                rowAboveBtn.setAttribute('title', 'Insert row above (cursor in a table cell)');
                rowAboveBtn.setAttribute('aria-label', 'Insert row above');
            }
            if (rowBelowBtn) {
                rowBelowBtn.setAttribute('title', 'Insert row below (cursor in a table cell)');
                rowBelowBtn.setAttribute('aria-label', 'Insert row below');
            }
            if (pdfBtn) {
                pdfBtn.setAttribute('title', 'Insert PDF (uploads when you save)');
                pdfBtn.setAttribute('aria-label', 'Insert PDF');
            }
            return !!(
                tableBtn &&
                pdfBtn &&
                colLeftBtn &&
                colRightBtn &&
                rowAboveBtn &&
                rowBelowBtn
            );
        };
        const t0 = window.setTimeout(() => {
            if (cancelled || tryLabel()) return;
            innerT = window.setTimeout(() => {
                if (!cancelled) tryLabel();
            }, 150);
        }, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(t0);
            window.clearTimeout(innerT);
        };
    }, []);

    useEffect(() => {
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    }, [content]);

    /** Older saved HTML may omit `data-pdf-label`; keep editor chip text in sync with the file name. */
    useEffect(() => {
        if (previewMode !== "editor") return;
        const editor = quillRef.current?.getEditor?.();
        if (!editor?.root || !String(content).includes("oa-pdf-embed")) return;
        editor.root.querySelectorAll(".oa-pdf-embed[data-pdf-url]").forEach((el) => {
            const path = sanitizeBlogPdfPath(el.getAttribute("data-pdf-url") || "");
            if (!path) return;
            el.setAttribute("data-pdf-label", blogPdfFileName(path));
            el.setAttribute("title", "Open PDF in new tab");
        });
    }, [content, previewMode]);

    /** Pending: local preview; saved embed: open server URL. */
    useEffect(() => {
        if (previewMode !== "editor") return undefined;
        const editor = quillRef.current?.getEditor?.();
        const root = editor?.root;
        if (!root) return undefined;

        const onPointerDown = (e) => {
            const pendingBlock = e.target?.closest?.(".oa-pdf-pending[data-pdf-pending-id]");
            if (pendingBlock && root.contains(pendingBlock)) {
                const id = pendingBlock.getAttribute("data-pdf-pending-id");
                const file = pendingMap.current?.get(id);
                if (!file) return;
                e.preventDefault();
                e.stopPropagation();
                const blobUrl = URL.createObjectURL(file);
                window.open(blobUrl, "_blank", "noopener,noreferrer");
                window.setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
                return;
            }

            const block = e.target?.closest?.(".oa-pdf-embed[data-pdf-url]");
            if (!block || !root.contains(block)) return;
            const path = sanitizeBlogPdfPath(block.getAttribute("data-pdf-url") || "");
            if (!path) return;
            const url = resolveBlogPdfUrl(path) || path;
            if (!url) return;
            e.preventDefault();
            e.stopPropagation();
            window.open(url, "_blank", "noopener,noreferrer");
        };

        root.addEventListener("pointerdown", onPointerDown, true);
        return () => root.removeEventListener("pointerdown", onPointerDown, true);
    }, [previewMode, content, pendingMap]);

    const previewHtml = useMemo(
        () => sanitizeBlogHtml(previewHtmlWithPendingNotes(content)),
        [content]
    );

    const modules = useMemo(
        () => ({
            table: true,
            toolbar: {
                container: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['link', 'blockquote', 'code-block'],
                    [
                        'insertTable',
                        'insertColumnLeft',
                        'insertColumnRight',
                        'insertRowAbove',
                        'insertRowBelow',
                        'insertPdf'
                    ],
                    ['clean']
                ],
                handlers: {
                    insertTable: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;

                        const tableModule = editor.getModule('table');
                        if (tableModule && typeof tableModule.insertTable === 'function') {
                            tableModule.insertTable(3, 3);
                            return;
                        }

                        const range = editor.getSelection(true);
                        const insertAt = range ? range.index : editor.getLength();
                        const tableHtml =
                            '<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">' +
                            '<tbody>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '<tr><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td><td style="border:1px solid #cbd5e1;padding:8px;">Cell</td></tr>' +
                            '</tbody></table><p><br/></p>';

                        editor.clipboard.dangerouslyPasteHTML(insertAt, tableHtml, 'user');
                    },
                    insertColumnLeft: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertColumnLeft) {
                            toast.error('Add column is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a column.');
                            return;
                        }
                        tableModule.insertColumnLeft();
                    },
                    insertColumnRight: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertColumnRight) {
                            toast.error('Add column is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a column.');
                            return;
                        }
                        tableModule.insertColumnRight();
                    },
                    insertRowAbove: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertRowAbove) {
                            toast.error('Add row is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a row.');
                            return;
                        }
                        tableModule.insertRowAbove();
                    },
                    insertRowBelow: () => {
                        const editor = quillRef.current?.getEditor();
                        if (!editor) return;
                        const tableModule = editor.getModule('table');
                        if (!tableModule?.getTable || !tableModule.insertRowBelow) {
                            toast.error('Add row is only available with the table editor enabled.');
                            return;
                        }
                        const [, , cell] = tableModule.getTable();
                        if (cell == null) {
                            toast.info('Click inside a table cell, then insert a row.');
                            return;
                        }
                        tableModule.insertRowBelow();
                    },
                    insertPdf: () => {
                        pdfInputRef.current?.click();
                    }
                }
            }
        }),
        []
    );

    const formats = [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'color',
        'background',
        'list',
        'bullet',
        'align',
        'link',
        'blockquote',
        'code-block',
        'table',
        'table-row',
        'table-cell',
        'oaPdf',
        'oaPdfPending'
    ];

    const handlePdfFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!String(file.name).toLowerCase().endsWith(".pdf")) {
            toast.error("Please choose a PDF file.");
            return;
        }
        const editor = quillRef.current?.getEditor();
        if (!editor) {
            toast.error("Editor is not ready yet.");
            return;
        }
        const id = crypto.randomUUID();
        pendingMap.current.set(id, file);
        const range = editor.getSelection(true);
        const insertAt = range ? range.index : editor.getLength();
        editor.insertEmbed(
            insertAt,
            "oaPdfPending",
            JSON.stringify({ id, name: file.name }),
            "user"
        );
        editor.setSelection(insertAt + 1, 0, "user");
        toast.success("PDF added — it uploads when you save the post.");
    };

    const handleEditorChange = (html, _delta, source, editor) => {
        // Keep Quill's raw HTML in state so typing (especially spaces) behaves correctly.
        setContent(html);
        if (source === 'user') setHasUserEdited(true);
        // IMPORTANT: send raw HTML upward; normalize at save-time (storage), not on each keystroke.
        if (onChange) onChange(html);

        const text = (editor.getText() || '').trim();
        const words = text ? text.split(/\s+/).length : 0;
        setWordCount(words);
        setCharCount(text.length);
    };

    return (
        <div className="rte-container">
            <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf,.pdf"
                className="rte-pdf-file-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={handlePdfFile}
            />
            <div className="rte-max-width">
                <div className="rte-shell">
                    <header className="rte-section-head">
                        <div className="rte-section-head-icon" aria-hidden>
                            <FileText size={22} strokeWidth={2} />
                        </div>
                        <div className="rte-section-head-copy">
                            <p className="rte-section-eyebrow">Body</p>
                            <h2 className="rte-section-title">Content editor</h2>
                            <p className="rte-section-lede">
                                Formatting, tables, insert PDF (uploads when you save), and HTML source when you need it.
                            </p>
                        </div>
                    </header>
                    <div className="rte-main-container">
                        <div className="rte-toolbar-section">

                            <button
                                type="button"
                                onClick={() =>
                                    setPreviewMode((prev) => (prev === 'editor' ? 'preview' : 'editor'))
                                }
                                className={`rte-button ${previewMode === 'preview' ? 'active' : ''}`}
                            >
                                {previewMode === 'editor' ? (
                                    <Eye size={15} strokeWidth={2} aria-hidden />
                                ) : (
                                    <PenLine size={15} strokeWidth={2} aria-hidden />
                                )}
                                <span>{previewMode === 'editor' ? 'Preview' : 'Editor'}</span>
                            </button>

                            {previewMode === "preview" && (
                                <button
                                    type="button"
                                    className={`rte-button ${showSourceCode ? 'active' : ''}`}
                                    onClick={() => setShowSourceCode((prev) => !prev)}
                                >
                                    {showSourceCode ? (
                                        <Eye size={15} strokeWidth={2} aria-hidden />
                                    ) : (
                                        <Code2 size={15} strokeWidth={2} aria-hidden />
                                    )}
                                    <span>{showSourceCode ? 'Preview HTML' : 'HTML source'}</span>
                                </button>
                            )}
                        </div>

                        <div className="rte-editor-container">
                            {previewMode === 'editor' && (
                                <div className="rte-editor-col">
                                    <label className="rte-col-label">Editor</label>
                                    <ReactQuill
                                        ref={quillRef}
                                        theme="snow"
                                        value={content}
                                        onChange={handleEditorChange}
                                        modules={modules}
                                        formats={formats}
                                        className="rte-quill"
                                    />
                                </div>
                            )}

                            {previewMode === 'preview' && (
                                <div className="rte-editor-col">
                                    <label className="rte-col-label">{showSourceCode ? 'HTML Source' : 'Preview'}</label>
                                    {showSourceCode ? (
                                        <textarea
                                            value={content}
                                            readOnly
                                            className="rte-source-code"
                                        />
                                    ) : (
                                        <div className="rte-preview ql-snow">
                                            <div
                                                className="ql-editor rte-preview-content"
                                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="rte-status-bar">
                            <div className="rte-stat-item">
                                <span>Words</span>
                                <span className="rte-stat-value">{wordCount}</span>
                            </div>
                            <div className="rte-stat-item">
                                <span>Characters</span>
                                <span className="rte-stat-value">{charCount}</span>
                            </div>
                            <div className="rte-stat-item rte-stat-item--clock">
                                <RteLiveClock />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
